import {
  BusinessCenterIcon,
  CalendarIcon,
  CityIcon,
  DocumentIcon,
  HomeIcon,
  ImessageIcon,
  OccupantsIcon,
  StarIcon,
  ToolsIcon,
  TravelFilledIcon,
  UtilitesIcon,
} from '@calizahq/react-components-v2'
import compact from 'lodash/compact'
import React, { FC, SVGProps, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { ReactComponent as DashboardIcon } from '@/assets/icons/dashboard.svg'
import { ReactComponent as TicketIcon } from '@/assets/icons/ticket.svg'
import { isV2SupportDeprecatedPath, V2_SUPPORT_DEPRECATED_FLAG } from '@/constants/featureFlags'
import { useFeatureFlagsContext } from '@/context'
import { AccessLevel, useAccessLevel } from '@/hooks/useAccessLevel'
import { APP_TYPE } from '@/types/User'

type Props = {
  activeApp: APP_TYPE
  onNavLinkPress: (path: string) => void
}

export interface MenuItem {
  Icon: FC<SVGProps<SVGSVGElement>>
  label: string
  path: string
}

export const MenuItems = ({ activeApp, onNavLinkPress }: Props) => {
  const { pathname } = useLocation()
  const { accessGranted } = useAccessLevel()
  const { AGM, HUB_MANAGER } = AccessLevel
  const { isEnabled } = useFeatureFlagsContext()
  const supportDeprecated = isEnabled(V2_SUPPORT_DEPRECATED_FLAG)

  const menuItems = useMemo(() => {
    const items = (() => {
      switch (activeApp) {
        case 'support':
          return compact([
            accessGranted([AGM, HUB_MANAGER]) ? { label: 'Dashboard', Icon: DashboardIcon, path: '/' } : null,
            accessGranted([AGM, HUB_MANAGER]) ? { label: 'Planner', Icon: CalendarIcon, path: '/planner' } : null,
            { label: 'Reservations', Icon: DocumentIcon, path: '/reservations' },
            { label: 'Members', Icon: OccupantsIcon, path: '/members' },
            { label: 'Properties', Icon: CityIcon, path: '/properties' },
            { label: 'Homes', Icon: HomeIcon, path: '/homes' },
            { label: 'Tickets', Icon: TicketIcon, path: '/tickets' },
            { label: 'Jobs', Icon: ToolsIcon, path: '/jobs' },
          ])
        case 'sales':
          return compact([
            { label: 'Dashboard', Icon: DashboardIcon, path: '/' },
            { label: 'People', Icon: OccupantsIcon, path: '/people' },
            { label: 'Corporate Leads', Icon: TravelFilledIcon, path: '/corporate-leads' },
            { label: 'Potential Bookings', Icon: StarIcon, path: '/potential-booking' },
            { label: 'Workspace', Icon: BusinessCenterIcon, path: '/workspace' },
          ])
        case 'settings':
          return compact([
            accessGranted([HUB_MANAGER]) ? { label: 'Ticket Types', Icon: UtilitesIcon, path: '/ticket-types' } : null,
          ])
        case 'communications':
          return compact([
            accessGranted([HUB_MANAGER])
              ? { label: 'Scheduled Types', Icon: CalendarIcon, path: '/scheduled-communication-types' }
              : null,
            accessGranted([HUB_MANAGER])
              ? { label: 'Scheduled Templates', Icon: ImessageIcon, path: '/scheduled-communication-templates' }
              : null,
          ])
        default:
          return compact([
            accessGranted([AGM, HUB_MANAGER]) ? { label: 'Dashboard', Icon: DashboardIcon, path: '/' } : null,
            accessGranted([AGM, HUB_MANAGER]) ? { label: 'Planner', Icon: CalendarIcon, path: '/planner' } : null,
            { label: 'Reservations', Icon: DocumentIcon, path: '/reservations' },
            { label: 'Members', Icon: OccupantsIcon, path: '/members' },
            { label: 'Properties', Icon: CityIcon, path: '/properties' },
            { label: 'Homes', Icon: HomeIcon, path: '/homes' },
            { label: 'Tickets', Icon: TicketIcon, path: '/tickets' },
            { label: 'Jobs', Icon: ToolsIcon, path: '/jobs' },
          ])
      }
    })()

    return supportDeprecated ? items.filter(({ path }) => !isV2SupportDeprecatedPath(path)) : items
  }, [AGM, HUB_MANAGER, accessGranted, activeApp, supportDeprecated])

  return (
    <>
      {menuItems.map(({ label, path, Icon }) => {
        let isActive = false

        if (label !== 'Dashboard') {
          isActive = pathname.includes(path)
        } else {
          isActive = pathname === path
        }

        return (
          <button className={`${isActive ? 'active-selection' : ''}`} key={label} onClick={() => onNavLinkPress(path)}>
            <li>
              {Icon && <Icon />}
              <span>{label}</span>
            </li>
          </button>
        )
      })}
    </>
  )
}
